import { css } from "@emotion/react";
import React, { useEffect, useMemo, useReducer } from "react";
import { ShimmerEffect } from "../ShimmerEffect";
import { useFieldGroup } from "../FieldGroup";
import { MediaFallback, MediaStyleProps } from "./MediaFallback";

export interface ThumbnailProps
  extends React.ComponentPropsWithoutRef<"img">, MediaStyleProps {
  imageRef?: React.RefObject<HTMLImageElement>;
  /**
   * src属性がundefinedの場合、noImageがtrueであればNoImageコンポーネントを表示します。
   */
  noImage?: boolean;
  /**
   * srcでURLを指定する代わりに、Fileオブジェクトを指定することができます。
   */
  file?: File;
  /**
   * Shimmerエフェクトを表示します。これはFieldGroupのloadingプロパティに相当します。
   * Thumbnailコンポーネントで直接利用されることは想定していません。
   */
  fieldLoading?: boolean;
}

export const Thumbnail = ({
  imageRef,
  src: propsSrc,
  file,
  alt,
  aspectRatio,
  width = "100%",
  height,
  className,
  style,
  noImage,
  fieldLoading: propsFieldLoading,
  ...props
}: ThumbnailProps) => {
  const [loaded, setLoaded] = useReducer(() => true, false);

  const objectUrl = useMemo(() => {
    return file ? URL.createObjectURL(file) : undefined;
  }, [file]);

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const src = objectUrl ?? propsSrc;

  const { loading: fieldLoading } = useFieldGroup({
    loading: propsFieldLoading,
  });

  if (src === undefined || fieldLoading) {
    const fallback = (
      <MediaFallback
        style={style}
        className={className}
        width={width}
        height={height}
        aspectRatio={aspectRatio}
      >
        No Image
      </MediaFallback>
    );
    if (noImage) {
      return fallback;
    } else {
      return <ShimmerEffect active>{fallback}</ShimmerEffect>;
    }
  }

  return (
    <ShimmerEffect active={loaded === false}>
      <img
        onLoad={setLoaded}
        style={style}
        className={className}
        css={[
          css`
            display: flex;
            object-fit: cover;
          `,
          // @see https://github.com/emotion-js/emotion/issues/3011
          css`
            aspect-ratio: ${aspectRatio};
          `,
        ]}
        ref={imageRef}
        src={src}
        alt={alt}
        width={width}
        height={height}
        {...props}
      />
    </ShimmerEffect>
  );
};
